// src/features/streams/schemas/StreamForm.schema.ts

import { z } from 'zod';
import { STREAM_RECORD_MODES, STREAM_OFFLINE_DISPLAY_MODES, STREAM_CONSTRAINTS } from '@/lib/constants/stream';
import { VIDEO_CONSTRAINTS, REF_ID_PATTERN, MEDIA_STATE_TYPES } from '@/lib/constants/media';

/** Stream Controls — recording, offline, captions */
export const StreamControlsSchema = z.object({
  recordMode: z.enum(STREAM_RECORD_MODES),
  recordOverride: z.boolean(),
  recordAppendTimeout: z.number()
    .min(STREAM_CONSTRAINTS.RECORD_APPEND_TIMEOUT_MIN)
    .max(STREAM_CONSTRAINTS.RECORD_APPEND_TIMEOUT_MAX),
  hasEmbeddedCaptions: z.boolean(),
  offlineDisplayMode: z.enum(STREAM_OFFLINE_DISPLAY_MODES),
  offlineMsgHtml: z.string()
    .max(STREAM_CONSTRAINTS.OFFLINE_MSG_MAX),
});

/** General Information — same as video form minus availability */
export const StreamGeneralInfoSchema = z.object({
  title: z.string()
    .min(1, 'Title is required')
    .max(VIDEO_CONSTRAINTS.TITLE_MAX, `Max ${VIDEO_CONSTRAINTS.TITLE_MAX} characters`),

  descShort: z.string()
    .max(VIDEO_CONSTRAINTS.DESC_SHORT_MAX, `Max ${VIDEO_CONSTRAINTS.DESC_SHORT_MAX} characters`),

  descLong: z.string()
    .max(VIDEO_CONSTRAINTS.DESC_LONG_MAX, `Max ${VIDEO_CONSTRAINTS.DESC_LONG_MAX} characters`),

  refId: z.string()
    .max(VIDEO_CONSTRAINTS.REF_ID_MAX, `Max ${VIDEO_CONSTRAINTS.REF_ID_MAX} characters`)
    .refine(
      (val) => !val || REF_ID_PATTERN.test(val),
      'Only letters, numbers, hyphens, and underscores allowed',
    ),

  published: z.number(),

  relatedLinkUrl: z.string()
    .max(VIDEO_CONSTRAINTS.RELATED_LINK_URL_MAX, `Max ${VIDEO_CONSTRAINTS.RELATED_LINK_URL_MAX} characters`)
    .refine(
      (val) => !val || /^https?:\/\/.+/.test(val),
      'Must be a valid URL starting with http:// or https://',
    ),

  relatedLinkText: z.string()
    .max(VIDEO_CONSTRAINTS.RELATED_LINK_TEXT_MAX, `Max ${VIDEO_CONSTRAINTS.RELATED_LINK_TEXT_MAX} characters`),

  mediaStateType: z.enum(MEDIA_STATE_TYPES),

  notes: z.string()
    .max(VIDEO_CONSTRAINTS.NOTES_MAX, `Max ${VIDEO_CONSTRAINTS.NOTES_MAX} characters`),

  tags: z.array(z.string()),
});

/** Combined form schema for Overview tab */
export const StreamOverviewSchema = StreamControlsSchema.merge(StreamGeneralInfoSchema);
export type StreamOverviewFormData = z.infer<typeof StreamOverviewSchema>;
